// src/pages/EventSettingsPage.js - Premium Event Settings Page
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { eventService } from '../services/api';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import PeopleIcon from '@mui/icons-material/People';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';

const EventSettingsPage = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [editValues, setEditValues] = useState({
    name: '',
    brideName: '',
    groomName: '',
    date: '',
    startDate: '',
    endDate: '',
    venue: '',
    budget: '',
    description: '',
    pointOfContactName: '',
    pointOfContactNumber: ''
  });
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [saveNotification, setSaveNotification] = useState({ show: false, field: '' });

  const fetchEventData = useCallback(async () => {
    try {
      const eventData = await eventService.getEvent(eventId);
      setEvent(eventData);
      setEditValues({
        name: eventData.name || '',
        brideName: eventData.brideName || '',
        groomName: eventData.groomName || '',
        date: eventData.date ? new Date(eventData.date).toISOString().split('T')[0] : '',
        startDate: eventData.startDate ? new Date(eventData.startDate).toISOString().split('T')[0] : '',
        endDate: eventData.endDate ? new Date(eventData.endDate).toISOString().split('T')[0] : '',
        venue: eventData.venue || '',
        budget: eventData.budget || '',
        description: eventData.description || '',
        pointOfContactName: eventData.pointOfContactName || '',
        pointOfContactNumber: eventData.pointOfContactNumber || ''
      });
    } catch (error) {
      console.error('Error fetching event data:', error);
      setError('Failed to load event data');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchEventData();
  }, [fetchEventData]);

  const handleStartEdit = (field) => {
    setEditing(field);
    setEditValues({
      name: event?.name || '',
      brideName: event?.brideName || '',
      groomName: event?.groomName || '',
      date: event?.date ? new Date(event.date).toISOString().split('T')[0] : '',
      startDate: event?.startDate ? new Date(event.startDate).toISOString().split('T')[0] : '',
      endDate: event?.endDate ? new Date(event.endDate).toISOString().split('T')[0] : '',
      venue: event?.venue || '',
      budget: event?.budget || '',
      description: event?.description || '',
      pointOfContactName: event?.pointOfContactName || '',
      pointOfContactNumber: event?.pointOfContactNumber || ''
    });
  };

  const handleSaveEdit = async () => {
    if (!editing) return;

    setSaving(true);
    try {
      const updateData = { [editing]: editValues[editing] };

      // Convert date string back to Date object if editing date fields
      if ((editing === 'date' || editing === 'startDate' || editing === 'endDate') && editValues[editing]) {
        updateData[editing] = new Date(editValues[editing]);
      }

      const updatedEvent = await eventService.updateEvent(eventId, updateData);
      setEvent(updatedEvent);
      setEditing(null);

      // Show save notification
      setSaveNotification({ show: true, field: editing });
      setTimeout(() => {
        setSaveNotification({ show: false, field: '' });
      }, 2000);
    } catch (error) {
      console.error('Error updating event:', error);
      setError('Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditing(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleDeleteEvent = async () => {
    if (deleteConfirmText !== 'delete') {
      return;
    }

    setDeleting(true);
    try {
      await eventService.deleteEvent(eventId);
      navigate('/');
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteConfirmText('');
  };

  const formatDate = (date) => {
    if (!date) return '';
    const eventDate = new Date(date);
    return eventDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderFormField = (field, label, icon, value, placeholder = '', type = 'text') => {
    const isEditing = editing === field;
    const displayValue = (field.includes('Date') || field === 'date') && value ? formatDate(value) : value;

    return (
      <motion.div
        className="premium-form-field"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <label className="premium-label">
          {React.cloneElement(icon, { className: 'premium-label-icon' })}
          <span className="premium-label-text">{label}</span>
        </label>
        <div className="premium-input-wrapper">
          {isEditing ? (
            <motion.input
              type={type}
              value={editValues[field]}
              onChange={(e) => setEditValues({...editValues, [field]: e.target.value})}
              onKeyDown={handleKeyPress}
              onBlur={handleSaveEdit}
              placeholder={placeholder}
              className="premium-input premium-input-editing"
              autoFocus
              initial={{ scale: 0.98 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            />
          ) : (
            <motion.div
              className="premium-input-display"
              onClick={() => handleStartEdit(field)}
              whileHover={{ backgroundColor: 'rgba(30, 81, 75, 0.03)' }}
              transition={{ duration: 0.2 }}
            >
              <span className={displayValue ? 'premium-value' : 'premium-placeholder'}>
                {displayValue || placeholder}
              </span>
              <motion.div
                className="premium-edit-icon"
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 1, scale: 1.1 }}
                transition={{ duration: 0.2 }}
              >
                <EditIcon style={{ fontSize: 16 }} />
              </motion.div>
            </motion.div>
          )}
        </div>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="event-overview-container">
        <div className="overview-header">
          <div className="header-left">
            <button
              className="back-button"
              onClick={() => navigate(`/event/${eventId}`)}
              title="Back to Event Details"
            >
              <ArrowBackIcon />
            </button>
            <div>
              <h3>Event Settings</h3>
              <p className="overview-subtitle">Loading...</p>
            </div>
          </div>
        </div>
        <div className="loading-message">Loading event settings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="event-overview-container">
        <div className="overview-header">
          <div className="header-left">
            <button
              className="back-button"
              onClick={() => navigate(`/event/${eventId}`)}
              title="Back to Event Details"
            >
              <ArrowBackIcon />
            </button>
            <div>
              <h3>Event Settings</h3>
              <p className="overview-subtitle">Error</p>
            </div>
          </div>
        </div>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="event-overview-container">
        <div className="error-message">Event not found</div>
      </div>
    );
  }

  const guestCount = event.guests?.length || 0;

  return (
    <motion.div
      className="premium-settings-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* Header */}
      <motion.div
        className="premium-settings-header"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.4 }}
      >
        <div className="premium-header-content">
          <motion.button
            className="premium-back-button"
            onClick={() => navigate(`/event/${eventId}`)}
            whileHover={{ x: -4 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
          >
            <ArrowBackIcon style={{ fontSize: 20 }} />
          </motion.button>
          <div>
            <h1 className="premium-page-title">Event Settings</h1>
            <p className="premium-page-subtitle">Manage your event details</p>
          </div>
        </div>
      </motion.div>

      {/* Settings Card */}
      <motion.div
        className="premium-settings-card"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.4 }}
      >
        {/* Section: Event Information */}
        <motion.div
          className="premium-section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, staggerChildren: 0.05 }}
        >
          <div className="premium-section-header">
            <h2 className="premium-section-title">Event Information</h2>
            <p className="premium-section-subtitle">Basic details about your event</p>
          </div>

          {renderFormField(
            'name',
            'Event Name',
            <EditIcon />,
            event.name,
            'Enter event name'
          )}

          <div className="premium-form-row">
            {renderFormField(
              'brideName',
              'Bride Name',
              <PersonIcon />,
              event.brideName,
              'Enter bride name'
            )}

            {renderFormField(
              'groomName',
              'Groom Name',
              <PersonIcon />,
              event.groomName,
              'Enter groom name'
            )}
          </div>
        </motion.div>

        {/* Divider */}
        <div className="premium-divider" />

        {/* Section: Schedule */}
        <motion.div
          className="premium-section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, staggerChildren: 0.05 }}
        >
          <div className="premium-section-header">
            <h2 className="premium-section-title">Schedule</h2>
            <p className="premium-section-subtitle">Event dates and timing</p>
          </div>

          <div className="premium-form-row">
            {renderFormField(
              'startDate',
              'Start Date',
              <CalendarTodayIcon />,
              event.startDate || event.date,
              'Select start date',
              'date'
            )}

            {renderFormField(
              'endDate',
              'End Date',
              <CalendarTodayIcon />,
              event.endDate || event.date,
              'Select end date',
              'date'
            )}
          </div>
        </motion.div>

        {/* Divider */}
        <div className="premium-divider" />

        {/* Section: Venue & Budget */}
        <motion.div
          className="premium-section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, staggerChildren: 0.05 }}
        >
          <div className="premium-section-header">
            <h2 className="premium-section-title">Venue & Budget</h2>
            <p className="premium-section-subtitle">Location and financial details</p>
          </div>

          {renderFormField(
            'venue',
            'Location',
            <LocationOnIcon />,
            event.venue,
            'Enter venue location'
          )}

          {renderFormField(
            'budget',
            'Budget',
            <CurrencyRupeeIcon />,
            event.budget,
            'Enter event budget',
            'number'
          )}
        </motion.div>

        {/* Divider */}
        <div className="premium-divider" />

        {/* Section: Contact */}
        <motion.div
          className="premium-section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, staggerChildren: 0.05 }}
        >
          <div className="premium-section-header">
            <h2 className="premium-section-title">Point of Contact</h2>
            <p className="premium-section-subtitle">Primary contact person</p>
          </div>

          <div className="premium-form-row">
            {renderFormField(
              'pointOfContactName',
              'Contact Name',
              <PersonIcon />,
              event.pointOfContactName,
              'Enter contact person name'
            )}

            {renderFormField(
              'pointOfContactNumber',
              'Contact Number',
              <ContactPhoneIcon />,
              event.pointOfContactNumber,
              'Enter contact phone number',
              'tel'
            )}
          </div>
        </motion.div>

        {/* Divider */}
        <div className="premium-divider" />

        {/* Section: Statistics */}
        <motion.div
          className="premium-section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          <div className="premium-section-header">
            <h2 className="premium-section-title">Statistics</h2>
            <p className="premium-section-subtitle">Event metrics</p>
          </div>

          <div className="premium-form-field">
            <label className="premium-label">
              <PeopleIcon className="premium-label-icon" />
              <span className="premium-label-text">Guest Count</span>
            </label>
            <div className="premium-input-wrapper">
              <div className="premium-input-display premium-readonly">
                <span className="premium-value">{guestCount}</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Divider */}
        <div className="premium-divider" />

        {/* Danger Zone */}
        <motion.div
          className="premium-section premium-danger-zone"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <div className="premium-section-header">
            <h2 className="premium-section-title">Danger Zone</h2>
            <p className="premium-section-subtitle">Irreversible actions</p>
          </div>

          <motion.button
            className="premium-delete-button"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={deleting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <DeleteIcon style={{ fontSize: 18 }} />
            Delete Event
          </motion.button>
        </motion.div>
      </motion.div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            className="premium-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCancelDelete}
          >
            <motion.div
              className="premium-modal-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="premium-modal-header">
                <h3 className="premium-modal-title">Delete Event</h3>
                <p className="premium-modal-subtitle">This action cannot be undone</p>
              </div>

              <div className="premium-modal-body">
                <div className="premium-warning-box">
                  <p className="premium-warning-text">
                    Deleting this event will permanently remove:
                  </p>
                  <ul className="premium-warning-list">
                    <li>All guest data ({guestCount} guests)</li>
                    <li>All RSVP information</li>
                    <li>All alert rules and scheduled messages</li>
                    <li>All event details and history</li>
                  </ul>
                </div>

                <div className="premium-form-field" style={{ marginTop: '24px' }}>
                  <label className="premium-label">
                    <span className="premium-label-text">Type "delete" to confirm</span>
                  </label>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="delete"
                    className="premium-input"
                    autoFocus
                  />
                </div>
              </div>

              <div className="premium-modal-footer">
                <button
                  className="premium-button-secondary"
                  onClick={handleCancelDelete}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  className="premium-button-danger"
                  onClick={handleDeleteEvent}
                  disabled={deleteConfirmText !== 'delete' || deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete Event'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Save Notification */}
      <AnimatePresence>
        {saveNotification.show && (
          <motion.div
            className="premium-save-notification"
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <CheckCircleIcon style={{ fontSize: 20, color: '#1e514b' }} />
            <span>Saved successfully</span>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default EventSettingsPage;
